import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Building2, Receipt, Settings, UserPlus, LogOut,
  KeyRound, Anchor, Eye, EyeOff, Stethoscope, Search, X, Ship,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import api, { apiError, fmtDateTime, fmtDate } from "../api";
import { Button, Input, Modal, Field, Badge, StatusBadge } from "./ui";

const NAV = {
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/candidates", label: "Candidates", icon: Users },
    { to: "/pandi", label: "P&I", icon: Ship },
    { to: "/admin/organizations", label: "Organizations", icon: Building2 },
    { to: "/billing", label: "Billing", icon: Receipt },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ],
  clinic: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/candidates", label: "Candidates", icon: Users },
    { to: "/candidates/new", label: "Register Candidate", icon: UserPlus },
  ],
  doctor: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/candidates", label: "Cases", icon: Stethoscope },
  ],
  client: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/candidates", label: "Candidates", icon: Users },
  ],
  accounts: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/candidates", label: "Candidates", icon: Users },
    { to: "/billing", label: "Billing", icon: Receipt },
  ],
  pandi: [
    { to: "/pandi", label: "P&I Dashboard", icon: Ship },
  ],
};

const ROLE_LABELS = { admin: "Admin", clinic: "Clinic", doctor: "Doctor", client: "Client", accounts: "Accounts", pandi: "P&I" };

function GlobalSearch() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) { setResults(null); setOpen(false); return; }
    const t = setTimeout(() => {
      api.get("/candidates", { params: { search: query.trim() } })
        .then((r) => { setResults(r.data.slice(0, 8)); setOpen(true); })
        .catch(() => { setResults([]); setOpen(true); });
    }, 400);
    return () => clearTimeout(t);
  }, [query]);

  const go = (c) => {
    setOpen(false);
    setQuery("");
    if (user.role === "clinic") navigate(`/candidates/${c.id}/edit?step=2`);
    else navigate(`/candidates/${c.id}`);
  };

  return (
    <div className="relative w-full max-w-md" ref={ref}>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <Input
        className="pl-9"
        placeholder="Search all candidates — name, mobile, INDOS, CDC, company ID, DOB"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => { if (results) setOpen(true); }}
        data-testid="global-search-input"
      />
      {open && results !== null && (
        <div className="absolute left-0 right-0 z-40 mt-2 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl fade-up" data-testid="global-search-dropdown">
          {results.length === 0 ? (
            <p className="px-4 py-4 text-center text-sm text-slate-400">No matching candidates found</p>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {results.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => go(c)}
                    className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                    data-testid={`global-search-result-${c.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-900">{c.name}</p>
                      <p className="truncate text-xs text-slate-500">
                        {c.mobile} · ID {c.company_id}{c.indos_number ? ` · INDOS ${c.indos_number}` : ""} · {c.rank} · DOB {fmtDate(c.dob)}
                      </p>
                    </div>
                    {user.role === "clinic" && (
                      <span className="shrink-0 text-xs font-bold text-teal">Upload documents →</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


function ChangePasswordModal({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed successfully");
      onClose();
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Change Password">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Current Password" required>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="current-password-input" />
        </Field>
        <Field label="New Password" required>
          <div className="relative">
            <Input type={show ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} data-testid="new-password-input" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="toggle-password-visibility">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Field>
        <Field label="Confirm New Password" required>
          <Input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="confirm-password-input" />
        </Field>
        <Button type="submit" disabled={busy} className="w-full" data-testid="change-password-submit-btn">
          {busy ? "Saving..." : "Change Password"}
        </Button>
      </form>
    </Modal>
  );
}

export default function Layout({ children }) {
  const { user, logout, logoUrl } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const nav = NAV[user.role] || [];

  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-navy-dark">
        <div className="flex items-center gap-3 px-5 py-5">
          <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-white p-1">
            {logoUrl ? (
              <img src={logoUrl} alt="MedVerifys" className="h-full w-full object-contain" data-testid="sidebar-logo" />
            ) : (
              <Anchor className="text-navy" size={22} />
            )}
          </div>
          <div>
            <p className="font-heading text-lg font-extrabold leading-tight text-white">MedVerifys</p>
          </div>
        </div>
        <nav className="mt-4 flex-1 space-y-1 px-3">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-teal text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`
              }
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <p className="truncate text-sm font-semibold text-white" data-testid="sidebar-user-name">{user.full_name}</p>
          <p className="truncate text-xs text-slate-400">
            {ROLE_LABELS[user.role]}{user.role !== "client" && user.organization_name ? ` · ${user.organization_name}` : ""}
          </p>
          <div className="mt-3 flex gap-2">
            {user.role === "admin" && (
              <button
                onClick={() => setPwOpen(true)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-white/10 px-2 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-white/20"
                data-testid="open-change-password-btn"
              >
                <KeyRound size={13} /> Password
              </button>
            )}
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-white/10 px-2 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-red-500/80 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </aside>

      <div className="ml-64 flex-1">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-6 border-b border-slate-200 bg-white px-8 py-3">
          <Badge color="navy" data-testid="topbar-role-badge" className="shrink-0">{ROLE_LABELS[user.role]} Portal</Badge>
          <GlobalSearch />

        </header>
        <main className="p-8">{children}</main>
      </div>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
